import { createFileRoute, useParams } from "@tanstack/react-router";
import { trpc } from "@/hooks/trpc";
import type {
  FermentationProtocolId,
  FermentationProtocolParameterId,
  FermentationParameterTypeId,
  FermentationLineTypeId,
} from "@brewbase/db/types";
import {
  FermParameterKindEnum,
  FermTextInputModeEnum,
} from "@brewbase/db/types";
import {
  formatValueSummary,
  isLineParameterCode,
  parameterVariableName,
  specificRateRule,
  validateRule,
} from "@brewbase/lib";
import { ParameterValueEditor } from "@/components/parameter-value-editor";
import { useSignalUnits } from "@/features/fermentation-live/use-signal-units";
import { displayConversionFor, displayString } from "@/features/fermentation-parameters/parameter-editor";
import type { ProtocolParameter } from "@/features/fermentation-protocol-editor/types";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { useCallback, useMemo, useState } from "react";
import { produce } from "immer";
import { useAutosave, filterDefined } from "@/hooks/use-autosave";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import {
  GripVerticalIcon,
  XIcon,
  HashIcon,
  TypeIcon,
  CheckSquareIcon,
  SettingsIcon,
} from "lucide-react";
import InlineEditableInput from "@/components/InlineEditableInput";
import { Link } from "@tanstack/react-router";
import { cn } from "@/lib/utils";
import { SingleLineFormulaEditor } from "@/components/single-line-formula-editor";

export const Route = createFileRoute(
  "/fermentations/protocols/$protocol_id/parameters",
)({
  component: ParametersPage,
});

const KIND_ICONS: Record<FermParameterKindEnum, React.ReactNode> = {
  [FermParameterKindEnum.NUMBER]: <HashIcon className="h-3.5 w-3.5" />,
  [FermParameterKindEnum.TEXT]: <TypeIcon className="h-3.5 w-3.5" />,
  [FermParameterKindEnum.CHECKBOX]: <CheckSquareIcon className="h-3.5 w-3.5" />,
};

// Input type for parameter updates
interface ParameterUpdateInput {
  protocolId: FermentationProtocolId;
  parameterTypeId: FermentationParameterTypeId;
  defaultNumberValue?: string | null;
  defaultTextValue?: string | null;
  defaultCheckboxValue?: boolean | null;
  defaultRampDurationH?: string | null;
  lineTypeId?: FermentationLineTypeId | null;
  isRequired?: boolean;
  isVisible?: boolean;
  instructions?: string | null;
  displayOrder?: number;
  formula?: string | null;
}

function ParametersPage() {
  const { protocol_id } = useParams({ from: Route.id });
  const protocolId = protocol_id as unknown as FermentationProtocolId;
  const utils = trpc.useUtils();

  // State
  const [selectedId, setSelectedId] =
    useState<FermentationProtocolParameterId | null>(null);
  const [isAddOpen, setIsAddOpen] = useState(false);

  // Fetch protocol data
  const { data: protocol, isLoading: isLoadingProtocol } =
    trpc.fermentations.protocols.get.useQuery(protocolId);

  // Fetch all parameter types for the add dialog
  const { data: allParameterTypes } =
    trpc.fermentationParameterTypes.list.useQuery();

  // A line-scoped type is only addable against a line, so the add combobox
  // needs the registry. A protocol has no procedure behind it, so every
  // declared line type is on offer -- which of them a given design actually
  // runs is settled when the protocol is applied.
  const { data: lineTypes } = trpc.fermentationLineTypes.list.useQuery();

  // Autosave for parameter updates - optimistic updates with debounced mutations
  const { save: saveParameter, flush: flushParameter } = useAutosave<
    typeof trpc.fermentations.protocols.get,
    ParameterUpdateInput
  >({
    mutation: trpc.fermentations.protocols.setParameter,
    query: trpc.fermentations.protocols.get,
    queryInput: protocolId,
    updateCache: (input, old) =>
      produce(old, (draft) => {
        if (!draft) return;
        // On the line as well as the type: a line parameter's type is shared
        // by every line, so matching on the type alone patches a sibling.
        const param = draft.parameters.find(
          (p) =>
            p.parameter_type_id === input.parameterTypeId &&
            (p.line_type_id ?? null) === (input.lineTypeId ?? null),
        );
        if (!param) return;
        // Map camelCase input to snake_case fields, filtering undefined
        Object.assign(
          param,
          filterDefined({
            default_number_value: input.defaultNumberValue,
            default_text_value: input.defaultTextValue,
            default_checkbox_value: input.defaultCheckboxValue,
            default_ramp_duration_h: input.defaultRampDurationH,
            line_type_id: input.lineTypeId,
            is_required: input.isRequired,
            is_visible: input.isVisible,
            instructions: input.instructions,
            formula: input.formula,
          }),
        );
      }),
    onError: (err) => toast.error(`Failed to update parameter: ${err.message}`),
  });

  // Direct mutation for formula saves (async, so editor can handle errors inline)
  const formulaMutation = trpc.fermentations.protocols.setParameter.useMutation(
    {
      onSuccess: () => {
        utils.fermentations.protocols.get.invalidate(protocolId);
      },
      // Suppress global error dialog — errors are shown inline in the formula editor
      onError: () => {},
    },
  );

  // Mutation for adding new parameters (transactional, not autosave)
  const addParameterMutation =
    trpc.fermentations.protocols.setParameter.useMutation({
      onSuccess: () => {
        utils.fermentations.protocols.get.invalidate(protocolId);
      },
      onError: (error) => {
        toast.error(`Failed to add parameter: ${error.message}`);
      },
    });

  const removeParameterMutation =
    trpc.fermentations.protocols.removeParameter.useMutation({
      onSuccess: () => {
        toast.success("Parameter removed");
        utils.fermentations.protocols.get.invalidate(protocolId);
        setSelectedId(null);
      },
      onError: (error) => {
        toast.error(`Failed to remove parameter: ${error.message}`);
      },
    });

  // The ramp's starting constant is its own row on the server, not a field of
  // the parameter, so it saves on its own rather than through the autosave.
  const rampStartMutation =
    trpc.fermentations.protocols.setRampStart.useMutation({
      onSuccess: () => {
        utils.fermentations.protocols.get.invalidate(protocolId);
      },
      onError: (error) => {
        toast.error(`Failed to set the start value: ${error.message}`);
        utils.fermentations.protocols.get.invalidate(protocolId);
      },
    });

  const reorderMutation =
    trpc.fermentations.protocols.reorderParameters.useMutation({
      onSuccess: () => {
        utils.fermentations.protocols.get.invalidate(protocolId);
      },
      onError: (error) => {
        toast.error(`Failed to reorder: ${error.message}`);
      },
    });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Get sorted parameters with type info
  const parameters: ProtocolParameter[] = useMemo(() => {
    if (!protocol?.parameters) return [];
    return [...protocol.parameters].sort((a, b) => {
      const orderDiff = (a.display_order ?? 0) - (b.display_order ?? 0);
      if (orderDiff !== 0) return orderDiff;
      return (a.id as number) - (b.id as number);
    });
  }, [protocol?.parameters]);

  // Group parameters by category
  const parametersByCategory = useMemo(() => {
    const grouped: Record<string, ProtocolParameter[]> = {};
    for (const param of parameters) {
      const category = param.parameterType.category ?? "Other";
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(param);
    }
    return grouped;
  }, [parameters]);

  /**
   * What can still be added, as (type, line) pairs.
   *
   * A line-scoped type is not one entry but one per line: `rate_sp` on the
   * methanol line and `rate_sp` on the nutrient line are different defaults,
   * and offering the bare type produced a row with no line -- which every
   * lookup then read as the fermentation-level one. Used-ness is therefore a
   * property of the pair, not of the type.
   */
  const addableParameters = useMemo(() => {
    if (!allParameterTypes) return [];
    const used = new Set(
      parameters.map((p) => `${p.parameter_type_id}:${p.line_type_id ?? 0}`),
    );
    const out: Array<{
      key: string;
      parameterTypeId: FermentationParameterTypeId;
      lineTypeId: FermentationLineTypeId | null;
      name: string;
      code: string;
      kind: FermParameterKindEnum;
      group: string;
    }> = [];
    for (const pt of allParameterTypes) {
      if (isLineParameterCode(pt.code)) {
        for (const lt of lineTypes ?? []) {
          const key = `${pt.id}:${lt.id}`;
          if (used.has(key)) continue;
          out.push({
            key,
            parameterTypeId: pt.id,
            lineTypeId: lt.id,
            name: `${lt.name} · ${pt.name}`,
            code: parameterVariableName(pt.code, lt.code),
            kind: pt.kind,
            group: pt.category ?? "Other",
          });
        }
      } else {
        const key = `${pt.id}:0`;
        if (used.has(key)) continue;
        out.push({
          key,
          parameterTypeId: pt.id,
          lineTypeId: null,
          name: pt.name,
          code: pt.code,
          kind: pt.kind,
          group: pt.category ?? "Other",
        });
      }
    }
    return out;
  }, [allParameterTypes, lineTypes, parameters]);

  // Selected parameter; the first row until someone picks one.
  const effectiveSelectedId = selectedId ?? parameters[0]?.id ?? null;
  const selectedParam = useMemo(() => {
    if (!effectiveSelectedId) return null;
    return parameters.find((p) => p.id === effectiveSelectedId) ?? null;
  }, [parameters, effectiveSelectedId]);

  /**
   * The rule grammar's world for this protocol (decision 0001): every
   * numeric type, line-scoped ones per declared line, under the names a rule
   * refers to. The server validates too; this gives the editor its answer
   * without a round trip.
   */
  const ruleVariables = useMemo(() => {
    const out: Array<{ code: string; name: string; unit?: string }> = [];
    for (const pt of allParameterTypes ?? []) {
      if (pt.kind !== FermParameterKindEnum.NUMBER) continue;
      if (isLineParameterCode(pt.code)) {
        for (const lt of lineTypes ?? []) {
          out.push({
            code: parameterVariableName(pt.code, lt.code),
            name: `${lt.name} · ${pt.name}`,
            unit: pt.unit ?? undefined,
          });
        }
      } else {
        out.push({ code: pt.code, name: pt.name, unit: pt.unit ?? undefined });
      }
    }
    return out.sort((a, b) => a.code.localeCompare(b.code));
  }, [allParameterTypes, lineTypes]);

  const knownCodes = useMemo(
    () => new Set(ruleVariables.map((v) => v.code)),
    [ruleVariables],
  );

  const validateFormula = useCallback(
    (param: ProtocolParameter, text: string): string[] => {
      const code = parameterVariableName(param.parameterType.code, param.lineCode);
      const rulesInForce = new Map<string, string>();
      for (const other of parameters) {
        if (other.id === param.id || !other.formula) continue;
        rulesInForce.set(
          parameterVariableName(other.parameterType.code, other.lineCode),
          other.formula,
        );
      }
      return validateRule(text, { code, knownCodes, rulesInForce });
    },
    [parameters, knownCodes],
  );

  // Handlers
  const handleDragEnd = (event: DragEndEvent) => {
    if (protocol?.is_locked) return;

    const { active, over } = event;
    if (over && active.id !== over.id) {
      const activeId = parseInt(
        active.id as string,
      ) as unknown as FermentationProtocolParameterId;
      const overId = parseInt(
        over.id as string,
      ) as unknown as FermentationProtocolParameterId;

      const oldIndex = parameters.findIndex((p) => p.id === activeId);
      const newIndex = parameters.findIndex((p) => p.id === overId);

      if (oldIndex !== -1 && newIndex !== -1) {
        const newOrder = [...parameters];
        const [moved] = newOrder.splice(oldIndex, 1);
        newOrder.splice(newIndex, 0, moved);

        reorderMutation.mutate({
          protocolId,
          orderedIds: newOrder.map((p) => p.id),
        });
      }
    }
  };

  // Combobox options for what can still be added. The value carries the line,
  // so a pick is a (type, line) pair rather than a type id.
  const addParameterOptions: ComboboxOption[] = useMemo(() => {
    return addableParameters.map((p) => ({
      value: p.key,
      label: (
        <span className="flex items-center gap-2">
          <span className="truncate">{p.name}</span>
          <code className="text-xs text-muted-foreground">{p.code}</code>
        </span>
      ),
      labelString: `${p.name} ${p.code}`,
      icon: KIND_ICONS[p.kind],
      group: p.group,
    }));
  }, [addableParameters]);

  const handleAddParameter = (key: string) => {
    const choice = addableParameters.find((p) => p.key === key);
    if (!choice) return;
    addParameterMutation.mutate(
      {
        protocolId,
        parameterTypeId: choice.parameterTypeId,
        lineTypeId: choice.lineTypeId,
      },
      {
        onSuccess: (data) => {
          toast.success("Parameter added");
          setIsAddOpen(false);
          selectParameter(data.id);
        },
      },
    );
  };

  // Update parameter via autosave - immediate optimistic update, debounced mutation
  /**
   * Select a row, flushing whatever the last one had pending.
   *
   * `useAutosave` merges every `save()` into a single debounced payload, and
   * that payload carries the row's identity as well as its fields. Moving to a
   * sibling within the debounce window therefore sent one mutation with the
   * new row's (type, line) and the old row's value — the edit landed on the
   * wrong parameter and was lost on the right one. Near-identical sibling rows
   * are exactly what a line-scoped type produces, so this is now easy to hit.
   */
  const selectParameter = (id: FermentationProtocolParameterId | null) => {
    if (id !== effectiveSelectedId) flushParameter();
    setSelectedId(id);
  };

  const handleUpdateParameter = (updates: Record<string, unknown>) => {
    if (!selectedParam || protocol?.is_locked) return;

    saveParameter({
      protocolId,
      parameterTypeId: selectedParam.parameter_type_id,
      // Half the row's identity. Without it the server matched on the type
      // alone, found a sibling line's default and rewrote its line away.
      lineTypeId: selectedParam.line_type_id as FermentationLineTypeId | null,
      ...updates,
    } as ParameterUpdateInput);
  };

  const handleRampStart = (value: string | null) => {
    if (!selectedParam || protocol?.is_locked) return;
    // A pending debounced edit may still hold the duration this depends on.
    flushParameter();
    rampStartMutation.mutate({
      protocolId,
      parameterTypeId: selectedParam.parameter_type_id,
      lineTypeId: selectedParam.line_type_id as FermentationLineTypeId | null,
      value,
    });
  };

  const handleSaveFormula = useCallback(
    async (value: string) => {
      if (!selectedParam || protocol?.is_locked) return;
      const problems = validateFormula(selectedParam, value);
      if (problems.length > 0) throw new Error(problems.join("; "));
      await formulaMutation.mutateAsync({
        protocolId,
        parameterTypeId: selectedParam.parameter_type_id,
        lineTypeId: selectedParam.line_type_id as FermentationLineTypeId | null,
        formula: value,
      });
    },
    [selectedParam, protocol?.is_locked, protocolId, formulaMutation, validateFormula],
  );

  /**
   * A line's absolute rate as a rule on its specific rate (decision 0001):
   * `<line>_rate_sp = <line>_specific_rate_sp * target_start_weight`, factor
   * free because both sides are canonical. Installs the rule on the line's
   * `rate_sp` row, creating the row when the protocol has none.
   */
  const handleDeriveRate = useCallback(
    (param: ProtocolParameter) => {
      if (protocol?.is_locked) return;
      const rule = specificRateRule(
        parameterVariableName(param.parameterType.code, param.lineCode),
      );
      const rateType = allParameterTypes?.find((pt) => pt.code === "rate_sp");
      if (!rule || !rateType || param.line_type_id == null) return;
      addParameterMutation.mutate(
        {
          protocolId,
          parameterTypeId: rateType.id,
          lineTypeId: param.line_type_id as FermentationLineTypeId,
          formula: rule.expression,
        },
        {
          onSuccess: (data) => {
            toast.success(`${param.lineName ?? "Line"} rate derived from its specific rate`);
            selectParameter(data.id);
          },
        },
      );
    },
    [protocol?.is_locked, allParameterTypes, addParameterMutation, protocolId],
  );

  const handleRemoveParameter = (
    parameterId: FermentationProtocolParameterId,
  ) => {
    if (protocol?.is_locked) return;

    removeParameterMutation.mutate({
      protocolId,
      parameterId,
    });
  };

  if (isLoadingProtocol) {
    return (
      <div className="p-4 space-y-3 h-full">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!protocol) {
    return <div className="p-4">Protocol not found</div>;
  }

  const isLocked = protocol.is_locked;

  return (
    <div className="flex flex-col h-full min-h-0">
      {parameters.length === 0 ? (
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="text-center text-muted-foreground">
            <p className="text-sm mb-3">No parameters defined.</p>
            {!isLocked && (
              <div className="flex items-center justify-center gap-2">
                <Combobox
                  options={addParameterOptions}
                  placeholder="Add parameter..."
                  searchPlaceholder="Search parameter types..."
                  emptyState="No parameter types available."
                  onValueChange={handleAddParameter}
                  triggerClassName="h-8 text-xs w-auto gap-1.5"
                  contentClassName="w-72"
                />
                <Link
                  to="/settings/fermentation-parameters"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  <SettingsIcon className="h-3.5 w-3.5" />
                  Manage types
                </Link>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="flex-1 min-h-0 grid grid-cols-1 md:grid-cols-12 container">
          {/* Left: List grouped by category */}
          <div className="md:col-span-5 min-h-0 border-r flex flex-col">
            {/* Table header */}
            <div className="grid grid-cols-[20px_1fr_minmax(80px,1fr)_24px] gap-1 items-center px-2 py-1 border-b bg-muted/40 text-sm text-muted-foreground font-medium flex-shrink-0">
              <div />
              <div>Parameter</div>
              <div className="text-right">Default</div>
              <div />
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="flex flex-col">
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={parameters.map((p) => p.id.toString())}
                    strategy={verticalListSortingStrategy}
                  >
                    {Object.entries(parametersByCategory).map(
                      ([category, categoryParams]) => (
                        <div key={category}>
                          <div className="pl-3 py-1.5 text-xs uppercase tracking-wider text-muted-foreground/70 border-l-2 border-muted-foreground/30 sticky top-0">
                            {category}
                          </div>
                          {categoryParams.map((param) => (
                            <ParameterTableRow
                              key={param.id}
                              param={param}
                              selected={effectiveSelectedId === param.id}
                              onClick={() => selectParameter(param.id)}
                              onDelete={() => handleRemoveParameter(param.id)}
                              readOnly={isLocked}
                            />
                          ))}
                        </div>
                      ),
                    )}
                  </SortableContext>
                </DndContext>
                {!isLocked && (
                  <div className="px-2 py-2 border-t flex-shrink-0 flex items-center gap-2">
                    <Combobox
                      options={addParameterOptions}
                      placeholder="Add parameter..."
                      searchPlaceholder="Search parameter types..."
                      emptyState="No parameter types available."
                      open={isAddOpen}
                      onOpenChange={setIsAddOpen}
                      disabled={addParameterMutation.isPending}
                      onValueChange={handleAddParameter}
                      contentClassName="w-72"
                    />
                    <Link
                      to="/settings/fermentation-parameters"
                      className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
                    >
                      <SettingsIcon className="h-3.5 w-3.5" />
                      Manage types
                    </Link>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>

          {/* Right: Inspector */}
          <div className="md:col-span-7 min-h-0">
            {selectedParam ? (
              <ParameterInspector
                key={selectedParam.id}
                param={selectedParam}
                parameters={parameters}
                ruleVariables={ruleVariables}
                onUpdate={handleUpdateParameter}
                onRampStart={handleRampStart}
                onSaveFormula={handleSaveFormula}
                onDeriveRate={handleDeriveRate}
                isLocked={isLocked}
              />
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-muted-foreground bg-muted/20">
                Select a parameter to edit.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ======================== Parameter Table Row ========================

function ParameterTableRow({
  param,
  selected,
  onClick,
  onDelete,
  readOnly,
}: {
  param: ProtocolParameter;
  selected: boolean;
  onClick: () => void;
  onDelete: () => void;
  readOnly: boolean;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: param.id.toString(), disabled: readOnly });
  const units = useSignalUnits();

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
    zIndex: isDragging ? 100 : "auto",
  } as React.CSSProperties;

  // Format value display
  const { display: valueDisplay, isLong: isLongValue } = useMemo(() => {
    const kind = param.parameterType.kind;
    if (kind === FermParameterKindEnum.NUMBER) {
      // Computed param with formula
      if (param.formula != null) {
        return {
          display: `ƒ ${param.formula}`,
          isLong: param.formula.length > 24,
        };
      }
      if (param.default_number_value != null) {
        // Stored canonical, read in the display unit: 0.002 g/g/h is 2 g/kg/h.
        const { displayUnit, toDisplay } = displayConversionFor(
          units,
          param.parameterType.code,
          param.parameterType.unit,
        );
        const summary = formatValueSummary(
          {
            numberValue: toDisplay(parseFloat(param.default_number_value)),
            rampDurationH:
              param.default_ramp_duration_h != null
                ? parseFloat(param.default_ramp_duration_h)
                : null,
          },
          displayUnit ?? "",
        );
        return { display: summary, isLong: summary.length > 30 };
      }
    } else if (kind === FermParameterKindEnum.CHECKBOX) {
      if (param.default_checkbox_value != null) {
        return {
          display: param.default_checkbox_value ? "✓" : "✗",
          isLong: false,
        };
      }
    } else if (kind === FermParameterKindEnum.TEXT) {
      if (param.default_text_value) {
        return {
          display:
            param.default_text_value.length > 24
              ? param.default_text_value.slice(0, 24) + "…"
              : param.default_text_value,
          isLong: param.default_text_value.length > 24,
        };
      }
    }
    return { display: "—", isLong: false };
  }, [param, units]);

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={
        "grid grid-cols-[20px_1fr_minmax(80px,1fr)_24px] gap-1 items-center px-2 py-1.5 text-xs cursor-pointer border-b border-border/50 transition-colors " +
        (selected
          ? "bg-primary/5 border-l-2 border-l-primary"
          : "hover:bg-muted/40")
      }
      onClick={onClick}
    >
      <button
        {...attributes}
        {...listeners}
        className="cursor-grab text-muted-foreground/50 hover:text-foreground flex-shrink-0 p-0.5 self-start mt-0.5"
        aria-label="Drag to reorder"
      >
        <GripVerticalIcon className="h-3 w-3" />
      </button>

      <div className="min-w-0 flex items-center gap-1.5">
        <span className="text-muted-foreground flex-shrink-0">
          {KIND_ICONS[param.parameterType.kind]}
        </span>
        {/* The line, not just the type: `rate_sp` on the methanol line and on
            the nutrient line are two rows with one type name. */}
        <span className="truncate text-sm">
          {param.lineName
            ? `${param.lineName} · ${param.parameterType.name}`
            : param.parameterType.name}
        </span>
        {param.is_required && (
          <span className="text-[10px] text-muted-foreground flex-shrink-0">
            *
          </span>
        )}
      </div>

      <div
        title={isLongValue ? valueDisplay : undefined}
        className={
          "font-mono text-xs tabular-nums min-w-0 text-right truncate" +
          (valueDisplay === "—"
            ? " text-muted-foreground/30"
            : " text-foreground")
        }
      >
        {valueDisplay}
      </div>

      <Button
        variant="ghost"
        size="sm"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        disabled={readOnly}
        className="h-5 w-5 p-0 text-muted-foreground/30 hover:text-destructive hover:bg-destructive/10"
      >
        <XIcon className="h-3 w-3" />
      </Button>
    </div>
  );
}

// ======================== Parameter Inspector ========================

function ParameterInspector({
  param,
  parameters,
  ruleVariables,
  onUpdate,
  onRampStart,
  onSaveFormula,
  onDeriveRate,
  isLocked,
}: {
  param: ProtocolParameter;
  parameters: ProtocolParameter[];
  ruleVariables: Array<{ code: string; name: string; unit?: string }>;
  onUpdate: (updates: Record<string, unknown>) => void;
  /** The constant the ramp starts from, or null to let it continue from whatever is in force. */
  onRampStart: (value: string | null) => void;
  onSaveFormula: (value: string) => Promise<void>;
  onDeriveRate: (param: ProtocolParameter) => void;
  isLocked: boolean;
}) {
  const pt = param.parameterType;
  const kind = pt.kind;
  const units = useSignalUnits();

  // Values are stored canonical (decision 0001). Protocol pages sit outside a
  // procedure, so they are read and typed in the dimension's default display
  // unit (the specific rate per kilogram), converted on the way in and out.
  const { displayUnit: shownUnit, toDisplay, toCanonical } = displayConversionFor(units, pt.code, pt.unit);
  const displayUnit = shownUnit ?? "";
  const isComputed = param.formula != null;

  // Switching a computed parameter to static discards its rule. Confirmed
  // inline; nothing else in the row changes.
  const [confirmStatic, setConfirmStatic] = useState(false);

  // The one-click derivation: a specific rate on a line whose absolute rate
  // is not already the canonical rule.
  const derivable = useMemo(() => {
    if (pt.code !== "specific_rate_sp" || param.line_type_id == null) return null;
    const rule = specificRateRule(parameterVariableName(pt.code, param.lineCode));
    if (!rule) return null;
    const rateRow = parameters.find(
      (p) =>
        p.parameterType.code === "rate_sp" &&
        (p.line_type_id ?? null) === (param.line_type_id ?? null),
    );
    return rateRow?.formula === rule.expression ? null : rule;
  }, [pt.code, param.line_type_id, param.lineCode, parameters]);

  // Available variables for the formula editor: the other numeric parameters in
  // this protocol, under the names formulas actually reference.
  const availableVariables = useMemo(() => {
    // The same world the validator accepts: every numeric parameter type,
    // line-scoped ones per declared line -- not just the rows this protocol
    // happens to carry. A rule may refer to a fermentation-level value like
    // target_start_weight that is never a protocol row, and offering only the
    // rows hid exactly those.
    const self = parameterVariableName(pt.code, param.lineCode);
    return ruleVariables.filter((v) => v.code !== self);
  }, [ruleVariables, pt.code, param.lineCode]);

  return (
    <ScrollArea className="h-full">
      <div className="p-4 space-y-4">
        {/* Parameter Type Info */}
        <div>
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Parameter Type
          </Label>
          <div className="flex items-center gap-2 mt-1">
            {KIND_ICONS[kind]}
            <span className="text-sm font-medium">
              {param.lineName ? `${param.lineName} · ${pt.name}` : pt.name}
            </span>
            <code className="text-xs bg-muted px-1 py-0.5 text-muted-foreground">
              {parameterVariableName(pt.code, param.lineCode)}
            </code>
            {pt.is_system && (
              <span className="text-xs bg-muted px-1 py-0.5 text-muted-foreground">
                System
              </span>
            )}
            {kind === FermParameterKindEnum.NUMBER && displayUnit && (
              <span className="text-xs font-mono text-muted-foreground">
                {displayUnit}
              </span>
            )}
          </div>
          {pt.description && (
            <p className="text-xs text-muted-foreground mt-1">
              {pt.description}
            </p>
          )}
        </div>

        {/* Divider */}
        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Default Value
          </Label>
          <p className="text-xs text-muted-foreground mb-2">
            Applied when this protocol is used for a fermentation
          </p>

          {/* Static/Computed toggle for numeric params */}
          {kind === FermParameterKindEnum.NUMBER && !isLocked && (
            <div className="flex gap-0 mb-3">
              <button
                className={cn(
                  "px-3 py-1 text-xs border transition-colors",
                  !isComputed
                    ? "bg-olive-3 text-foreground border-olive-7"
                    : "bg-white text-muted-foreground border-olive-5 hover:bg-olive-2",
                )}
                onClick={() => {
                  if (!isComputed) return;
                  if (param.formula && param.formula.trim() !== "") {
                    setConfirmStatic(true);
                  } else {
                    onUpdate({ formula: null });
                  }
                }}
              >
                Static
              </button>
              <button
                className={cn(
                  "px-3 py-1 text-xs border-y border-r transition-colors",
                  isComputed
                    ? "bg-olive-3 text-foreground border-olive-7"
                    : "bg-white text-muted-foreground border-olive-5 hover:bg-olive-2",
                )}
                onClick={() => {
                  if (!isComputed) {
                    onUpdate({ formula: "", defaultNumberValue: null, defaultRampDurationH: null });
                  }
                }}
              >
                Computed
              </button>
            </div>
          )}

          {confirmStatic && (
            <div className="border border-olive-6 bg-olive-2 p-2 mb-3 text-xs space-y-2">
              <p>
                Switching <span className="font-medium">{pt.name}</span> to a
                static value discards its rule{" "}
                <code className="font-mono">{param.formula}</code>.
              </p>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-6 text-xs px-2"
                  onClick={() => setConfirmStatic(false)}
                >
                  Keep
                </Button>
                <Button
                  size="sm"
                  className="h-6 text-xs px-2"
                  onClick={() => {
                    setConfirmStatic(false);
                    onUpdate({ formula: null });
                  }}
                >
                  Discard rule
                </Button>
              </div>
            </div>
          )}

          <div className="mt-2">
            {kind === FermParameterKindEnum.NUMBER && isComputed && (
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">
                    Rule
                  </Label>
                  <SingleLineFormulaEditor
                    value={param.formula ?? ""}
                    onSave={onSaveFormula}
                    disabled={isLocked}
                    availableVariables={availableVariables}
                    className="mt-1"
                  />
                  <p className="text-xs text-muted-foreground/70 mt-1">
                    Evaluated in canonical units; result in {pt.unit ?? "the type's unit"}
                  </p>
                </div>
              </div>
            )}

            {kind === FermParameterKindEnum.NUMBER && !isComputed && pt.value_source !== "authored" && (
              <p className="text-xs text-muted-foreground">
                {pt.value_source === "measured"
                  ? `Left empty, read from ${pt.source_attribute} when the protocol is applied.`
                  : "Left empty, the operator is asked for it when the protocol is applied."}
              </p>
            )}

            {kind === FermParameterKindEnum.NUMBER && !isComputed && pt.ramps && (
              <ParameterValueEditor
                value={{
                  numberValue:
                    param.default_number_value != null
                      ? toDisplay(parseFloat(param.default_number_value))
                      : null,
                  rampDurationH:
                    param.default_ramp_duration_h != null
                      ? parseFloat(param.default_ramp_duration_h)
                      : null,
                }}
                unit={displayUnit}
                onChange={(value) =>
                  onUpdate({
                    defaultNumberValue:
                      value.numberValue != null
                        ? toCanonical(String(value.numberValue))
                        : null,
                    defaultRampDurationH:
                      value.rampDurationH != null
                        ? String(value.rampDurationH)
                        : null,
                  })
                }
                from={
                  param.ramp_from_value != null
                    ? toDisplay(parseFloat(param.ramp_from_value))
                    : null
                }
                onFromChange={(value) =>
                  onRampStart(value != null ? toCanonical(String(value)) : null)
                }
                disabled={isLocked}
              />
            )}

            {kind === FermParameterKindEnum.NUMBER && !isComputed && !pt.ramps && (
              <div className="flex gap-2 items-center">
                <InlineEditableInput
                  numeric
                  value={displayString(param.default_number_value ?? "", toDisplay)}
                  onSave={(value) =>
                    onUpdate({ defaultNumberValue: value ? toCanonical(value) : null })
                  }
                  placeholder="Value"
                  inputClassName="text-sm font-mono h-9 w-32"
                  disabled={isLocked}
                />
                {displayUnit && (
                  <span className="text-sm font-mono text-muted-foreground">
                    {displayUnit}
                  </span>
                )}
              </div>
            )}

            {kind === FermParameterKindEnum.CHECKBOX && (
              <div className="flex items-center gap-2 py-1">
                <Checkbox
                  id="default-checkbox"
                  checked={param.default_checkbox_value ?? false}
                  onCheckedChange={(checked) =>
                    onUpdate({ defaultCheckboxValue: !!checked })
                  }
                  disabled={isLocked}
                />
                <Label
                  htmlFor="default-checkbox"
                  className="text-sm font-normal cursor-pointer"
                >
                  {param.default_checkbox_value ? "Checked" : "Unchecked"}
                </Label>
              </div>
            )}

            {kind === FermParameterKindEnum.TEXT && (
              <TextValueEditor
                param={param}
                onUpdate={onUpdate}
                isLocked={isLocked}
              />
            )}
          </div>

          {/* Validation info */}
          {kind === FermParameterKindEnum.NUMBER &&
            (pt.min_value || pt.max_value) && (
              <p className="text-xs text-muted-foreground mt-2">
                Valid range: {pt.min_value ?? "—"} to {pt.max_value ?? "—"}
              </p>
            )}

          {derivable && !isLocked && (
            <div className="mt-3 flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                className="h-7 text-xs"
                onClick={() => onDeriveRate(param)}
              >
                Derive rate from specific rate
              </Button>
              <code className="text-xs text-muted-foreground">
                {derivable.signal} = {derivable.expression}
              </code>
            </div>
          )}
        </div>

        {/* Options */}
        <div className="border-t pt-4 space-y-3">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Options
          </Label>

          <div className="flex items-center gap-2">
            <Checkbox
              id="is-required"
              checked={param.is_required}
              onCheckedChange={(checked) => onUpdate({ isRequired: !!checked })}
              disabled={isLocked}
            />
            <Label
              htmlFor="is-required"
              className="text-sm font-normal cursor-pointer"
            >
              Required — must have value when using this protocol
            </Label>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="is-visible"
              checked={param.is_visible}
              onCheckedChange={(checked) => onUpdate({ isVisible: !!checked })}
              disabled={isLocked}
            />
            <Label
              htmlFor="is-visible"
              className="text-sm font-normal cursor-pointer"
            >
              Visible — show in fermentation parameter list
            </Label>
          </div>
        </div>

        {/* Instructions */}
        <div className="border-t pt-4">
          <Label className="text-xs uppercase text-muted-foreground font-medium">
            Instructions
          </Label>
          <InlineEditableInput
            value={param.instructions ?? ""}
            onSave={(value) => onUpdate({ instructions: value || null })}
            placeholder="Add instructions for this parameter..."
            inputClassName="text-sm mt-1"
            disabled={isLocked}
          />
        </div>
      </div>
    </ScrollArea>
  );
}

// ======================== Text Value Editor ========================

function TextValueEditor({
  param,
  onUpdate,
  isLocked,
}: {
  param: ProtocolParameter;
  onUpdate: (updates: Record<string, unknown>) => void;
  isLocked: boolean;
}) {
  const pt = param.parameterType;
  const inputMode = pt.text_input_mode ?? FermTextInputModeEnum.FREE_TEXT;
  const textOptions = pt.text_options ?? [];

  if (inputMode === FermTextInputModeEnum.DROPDOWN && textOptions.length > 0) {
    return (
      <Select
        value={param.default_text_value ?? ""}
        onValueChange={(value) => onUpdate({ defaultTextValue: value || null })}
        disabled={isLocked}
      >
        <SelectTrigger>
          <SelectValue placeholder="Select value..." />
        </SelectTrigger>
        <SelectContent>
          {textOptions.map((opt) => (
            <SelectItem key={opt} value={opt}>
              {opt}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    );
  }

  return (
    <Input
      value={param.default_text_value ?? ""}
      onChange={(e) => onUpdate({ defaultTextValue: e.target.value || null })}
      placeholder="Enter value..."
      disabled={isLocked}
    />
  );
}
